import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useFormContext, Controller, FieldErrors, Path, get, useFieldArray, useWatch } from 'react-hook-form';
import { ChevronDown, ChevronUp, Save, Trash2, Plus } from 'lucide-react';

import { OrderSchemaType, ItemOrderSchemaType } from '../../order.schema';
import { ApiItems } from '../../types';
import { FIELD_LABELS, SECTION_LABELS, createItemInitialState } from '../../constants';

import { Button, CircularCollapseIcon, Separator, FormGrid, FormGroup, Select, Label, Card, Title } from '../../style';
import { Summary, SummaryItem, SummaryLabel, SummaryValue, ActionsDiv, DeleteDiv, SaveIcon, ActionButtonsContainer, CollapsibleItemHeader } from './style';

import ConfirmationModal from '../../../../../../components/ModalConfirmation';
import AddItemModal from './AddItemModal';
import SubItem from './SubItem';

import { FormMaterial } from './components/Forms/FormMaterial';
import { FormProduct } from './components/Forms/FormProduct';
import { FormService } from './components/Forms/FormService';
import { FormResource } from './components/Forms/FormResource';
import PricesSection from './PricesSection';

const ItemSummary: React.FC<{ item: ItemOrderSchemaType | undefined }> = ({ item }) => {
  const countByType = (mainItem: ItemOrderSchemaType | undefined): { P: number; S: number; I: number; M: number } => {
    const counts = { P: 0, S: 0, I: 0, M: 0 };
    if (!mainItem) return counts;
    switch (mainItem.itemType) {
      case 'PRODUCT': counts.P += 1; break;
      case 'SERVICE': counts.S += 1; break;
      case 'RESOURCE': counts.I += 1; break;
      case 'MATERIAL': counts.M += 1; break;
      default: break;
    }
    if (Array.isArray(mainItem.subItems)) {
      mainItem.subItems.forEach((sub: ItemOrderSchemaType) => {
        switch (sub.itemType) {
          case 'PRODUCT': counts.P += 1; break;
          case 'SERVICE': counts.S += 1; break;
          case 'RESOURCE': counts.I += 1; break;
          case 'MATERIAL': counts.M += 1; break;
          default: break;
        }
      });
    }
    return counts;
  };

  const counts = countByType(item);
  const mainProduct = item?.offeredProductMaterial?.product || item?.requestedProductMaterial?.product || item?.finalProduct?.produto || item?.resource?.insumo || item?.feedstock?.materiaPrima || '';

  const typeLabel = (() => {
    switch (item?.itemType) {
      case 'PRODUCT': return 'Produto Final';
      case 'SERVICE': return 'Serviço';
      case 'RESOURCE': return 'Insumos';
      case 'MATERIAL': return 'Matéria Prima';
      default: return '';
    }
  })();

  const productsArr = [
    counts.P > 0 ? `P: ${counts.P}` : '',
    counts.S > 0 ? `S: ${counts.S}` : '',
    counts.I > 0 ? `I: ${counts.I}` : '',
    counts.M > 0 ? `M: ${counts.M}` : ''
  ].filter(Boolean);

  let productsLine = '';
  if (productsArr.length === 1) {
    productsLine = productsArr[0];
  } else if (productsArr.length === 2) {
    productsLine = productsArr.join(' e ');
  } else if (productsArr.length === 3) {
    productsLine = productsArr.slice(0, 2).join(', ') + ' e ' + productsArr[2];
  } else if (productsArr.length === 4) {
    productsLine = productsArr.slice(0, 3).join(', ') + ' e ' + productsArr[3];
  }

  const totalItems = counts.P + counts.S + counts.I + counts.M;

  let mainQty = 0;
  if (item?.itemType === 'MATERIAL') {
    mainQty = Number(item?.feedstock?.quantidade) || 0;
  } else if (item?.itemType === 'PRODUCT') {
    mainQty = Number(item?.finalProduct?.quantidade) || 0;
  } else if (item?.itemType === 'RESOURCE') {
    mainQty = Number(item?.resource?.quantidade) || 0;
  } else if (item?.itemType === 'SERVICE') {
    mainQty = Number(item?.stage?.quantidade) || 0;
  }

  let subQty = 0;
  if (Array.isArray(item?.subItems)) {
    item.subItems.forEach((sub: ItemOrderSchemaType) => {
      if (sub.itemType === 'MATERIAL') {
        subQty += Number(sub?.feedstock?.quantidade) || 0;
      } else if (sub.itemType === 'PRODUCT') {
        subQty += Number(sub?.finalProduct?.quantidade) || 0;
      } else if (sub.itemType === 'RESOURCE') {
        subQty += Number(sub?.resource?.quantidade) || 0;
      } else if (sub.itemType === 'SERVICE') {
        subQty += Number(sub?.stage?.quantidade) || 0;
      }
    });
  }
  const totalQty = mainQty + subQty;

  return (
    <Summary>
      <SummaryItem style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '16px', whiteSpace: 'nowrap' }}>
        <SummaryLabel>Item Principal:</SummaryLabel>
        <SummaryValue>{typeLabel}</SummaryValue>
      </SummaryItem>
      <SummaryItem>
        <SummaryLabel>Quantidade de produtos:</SummaryLabel>
        <SummaryValue>{totalItems}</SummaryValue>
      </SummaryItem>
      <SummaryItem>
        <SummaryItem style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '16px', whiteSpace: 'nowrap' }}>
          {productsLine && (
            <>
              <SummaryLabel>Totais por tipo de produto:</SummaryLabel>
              <SummaryValue>{productsLine}</SummaryValue>
            </>
          )}
        </SummaryItem>
        <SummaryLabel>Qtd.:</SummaryLabel>
        <SummaryValue>{totalQty > 0 ? totalQty : ''}</SummaryValue>
      </SummaryItem>
      <SummaryItem><SummaryLabel>Subtotal:</SummaryLabel><SummaryValue>{item?.priceDetails?.itemSubtotal ? `R$ ${item.priceDetails.itemSubtotal.toFixed(2)}` : 'N/A'}</SummaryValue></SummaryItem>
      <SaveIcon $isSaved={!!item?.isSaved}><Save size={16} /></SaveIcon>
    </Summary>
  );
};

interface ItemsSectionProps {
  index: number;
  onDelete: () => void;
  updateItem: (index: number, value: ItemOrderSchemaType) => void;
  isCollapsed: boolean;
  onToggle: (forceOpen?: boolean) => void;
  itemOptionsFromApi: ApiItems[];
  formatOptionsList: string[];
  finishingOptionsList: string[];
  highlightSaveButton?: boolean;
}

export const ItemsSection: React.FC<ItemsSectionProps> = ({
  index,
  onDelete,
  updateItem,
  isCollapsed,
  onToggle,
  itemOptionsFromApi,
  formatOptionsList,
  finishingOptionsList,
  highlightSaveButton = false
}) => {
  const itemPath = `items.${index}` as const;
  const { control, watch, setValue, trigger, formState: { errors }, getValues } = useFormContext<OrderSchemaType>();

  const itemType = useWatch({ control, name: `${itemPath}.itemType` });
  const currentItem = watch(itemPath);
  const isItemSaved = currentItem?.isSaved;

  const feedstockQty = useWatch({ control, name: `${itemPath}.feedstock.quantidade` });
  const finalProductQty = useWatch({ control, name: `${itemPath}.finalProduct.quantidade` });
  const resourceQty = useWatch({ control, name: `${itemPath}.resource.quantidade` });
  const stageQty = useWatch({ control, name: `${itemPath}.stage.quantidade` });
  const subItems = useWatch({ control, name: `${itemPath}.subItems` });

  const quantity = useMemo(() => {
    let mainQty = 0;
    switch (itemType) {
      case 'MATERIAL':
        mainQty = Number(feedstockQty) || 0;
        break;
      case 'PRODUCT':
        mainQty = Number(finalProductQty) || 0;
        break;
      case 'RESOURCE':
        mainQty = Number(resourceQty) || 0;
        break;
      case 'SERVICE':
        mainQty = Number(stageQty) || 0;
        break;
    }

    let subQty = 0;
    if (Array.isArray(subItems)) {
      subItems.forEach((sub: ItemOrderSchemaType) => {
        switch (sub.itemType) {
          case 'MATERIAL':
            subQty += Number(sub.feedstock?.quantidade) || 0;
            break;
          case 'PRODUCT':
            subQty += Number(sub.finalProduct?.quantidade) || 0;
            break;
          case 'RESOURCE':
            subQty += Number(sub.resource?.quantidade) || 0;
            break;
          case 'SERVICE':
            subQty += Number(sub.stage?.quantidade) || 0;
            break;
        }
      });
    }

    return mainQty + subQty;
  }, [itemType, feedstockQty, finalProductQty, resourceQty, stageQty, subItems]);

  const handleItemTypeChange = (newType: 'MATERIAL' | 'PRODUCT' | 'RESOURCE' | 'SERVICE') => {
    const oldItem = getValues(itemPath);
    const newItem = createItemInitialState(newType, oldItem.isPrincipal);
    newItem.isSaved = oldItem.isSaved;
    newItem.subItems = oldItem.subItems;
    setValue(itemPath, newItem, { shouldValidate: false });
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationErrorMessages, setValidationErrorMessages] = useState<string[]>([]);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const initialItemStateRef = useRef<ItemOrderSchemaType | null>(null);

  const { fields: subItemFields, append: appendSubItem, remove: removeSubItem } = useFieldArray({
    control,
    name: `${itemPath}.subItems`,
  });

  useEffect(() => {
    if (!isCollapsed && (!initialItemStateRef.current || isItemSaved)) {
      initialItemStateRef.current = JSON.parse(JSON.stringify(getValues(itemPath)));
    }
  }, [isCollapsed, itemPath, getValues, isItemSaved]);

  const handleConfirmDelete = () => { onDelete(); setShowDeleteConfirm(false); };
  const handleCancelClick = () => { setShowCancelConfirm(true); };

  const handleConfirmCancel = () => {
    if (isItemSaved) {
      if (initialItemStateRef.current) {
        updateItem(index, initialItemStateRef.current);
      }
      onToggle();
    } else {
      onDelete();
    }
    setShowCancelConfirm(false);
  };

  const handleAddSubItem = (type: 'MATERIAL' | 'PRODUCT' | 'RESOURCE' | 'SERVICE') => {
    const newSubItem = createItemInitialState(type, false);
    appendSubItem(newSubItem);
    setShowAddItemModal(false);
  };

  const handleRemoveSubItem = (subItemIndex: number) => {
    removeSubItem(subItemIndex);
  };

  const extractAndFormatErrorMessages = (errorsObject: FieldErrors<ItemOrderSchemaType>): string[] => {
    const messages: string[] = [];

    const findErrorMessages = (obj: any, path: string[] = []) => {
      if (!obj) return;

      if (typeof obj.message === 'string') {
        const fieldName = path.length > 0 ? path[path.length - 1] : 'desconhecido';
        const sectionName = path.length > 1 ? path[path.length - 2] : 'Geral';

        const readableFieldName = FIELD_LABELS[fieldName] || fieldName;
        const readableSectionName = SECTION_LABELS[sectionName] || sectionName;

        messages.push(`${obj.message}`);
        return;
      }

      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          findErrorMessages(obj[key], [...path, key]);
        }
      }
    };

    findErrorMessages(errorsObject);
    return [...new Set(messages)];
  };

  const handleSaveClick = async () => {
    const isValid = await trigger(itemPath);
    if (isValid) {
      const currentItemData = getValues(itemPath);
      const updatedSubItems = currentItemData.subItems?.map((subItem: any) => ({
        ...subItem,
        isSaved: true
      })) || [];

      let itemQuantity = 0;
      if (itemType === 'MATERIAL') {
        itemQuantity = Number(currentItemData.feedstock?.quantidade) || 0;
      } else if (itemType === 'PRODUCT') {
        itemQuantity = Number(currentItemData.finalProduct?.quantidade) || 0;
      } else if (itemType === 'RESOURCE') {
        itemQuantity = Number(currentItemData.resource?.quantidade) || 0;
      } else if (itemType === 'SERVICE') {
        itemQuantity = Number(currentItemData.stage?.quantidade) || 0;
      }

      const itemToSave = {
        ...currentItemData,
        isSaved: true,
        quantity: itemQuantity,
        stageId: currentItemData.stageId,
        materialId: currentItemData.materialId,
        productId: currentItemData.productId,
        resourceId: currentItemData.resourceId,
        serviceCategoryId: currentItemData.serviceCategoryId,
        serviceTypeId: currentItemData.serviceTypeId,
        resourceCategoryId: currentItemData.resourceCategoryId,
        resourceTypeId: currentItemData.resourceTypeId,
        serviceSupplierCompanyId: currentItemData.serviceSupplierCompanyId,
        resourceSupplierCompanyId: currentItemData.resourceSupplierCompanyId,
        scriptId: currentItemData.scriptId,
        subItems: updatedSubItems.map((subItem: any) => {
          let subQuantity = 0;
          if (subItem.itemType === 'MATERIAL') {
            subQuantity = Number(subItem.feedstock?.quantidade) || 0;
          } else if (subItem.itemType === 'PRODUCT') {
            subQuantity = Number(subItem.finalProduct?.quantidade) || 0;
          } else if (subItem.itemType === 'RESOURCE') {
            subQuantity = Number(subItem.resource?.quantidade) || 0;
          } else if (subItem.itemType === 'SERVICE') {
            subQuantity = Number(subItem.stage?.quantidade) || 0;
          }
          return {
            ...subItem,
            quantity: subQuantity,
            stageId: subItem.stageId,
            materialId: subItem.materialId,
            productId: subItem.productId,
            resourceId: subItem.resourceId,
            serviceCategoryId: subItem.serviceCategoryId,
            serviceTypeId: subItem.serviceTypeId,
            resourceCategoryId: subItem.resourceCategoryId,
            resourceTypeId: subItem.resourceTypeId,
            serviceSupplierCompanyId: subItem.serviceSupplierCompanyId,
            resourceSupplierCompanyId: subItem.resourceSupplierCompanyId,
            scriptId: subItem.scriptId
          };
        })
      };

      itemToSave.priceDetails = currentItemData.priceDetails || {};

      updateItem(index, itemToSave);
      onToggle();
    } else {
      const itemErrors = get(errors, itemPath) as FieldErrors<ItemOrderSchemaType> | undefined;
      const messages = itemErrors ? extractAndFormatErrorMessages(itemErrors) : [];
      setValidationErrorMessages(messages.length > 0 ? messages : ["Existem erros de validação. Verifique os campos destacados."]);
      setShowValidationModal(true);
      const currentItemData = getValues(itemPath);
    }
  };

  const renderFormByType = () => {
    switch (itemType) {
      case 'MATERIAL':
        return <FormMaterial
          itemPath={itemPath}
          itemOptionsFromApi={itemOptionsFromApi}
          formatOptionsList={formatOptionsList}
          finishingOptionsList={finishingOptionsList}
        />;
      case 'PRODUCT':
        return <FormProduct itemPath={itemPath} />;
      case 'RESOURCE':
        return <FormResource itemPath={itemPath} />;
      case 'SERVICE':
        return <FormService itemPath={itemPath} />;
      default:
        return <div style={{ padding: '20px', textAlign: 'center' }}>Selecione um tipo de item para continuar.</div>;
    }
  };

  return (
    <>
      <CollapsibleItemHeader>
        {isCollapsed ? (
          <ItemSummary item={currentItem} />
        ) : (
          <Title>Itens</Title>
        )}
        <ActionsDiv>
          <DeleteDiv onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(true); }}><Trash2 size={20} /></DeleteDiv>
          <CircularCollapseIcon
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onToggle();
            }}
          >
            {isCollapsed ? <ChevronDown size={24} /> : <ChevronUp size={24} />}
          </CircularCollapseIcon>
        </ActionsDiv>
      </CollapsibleItemHeader>

      {!isCollapsed && (
        <>
          <FormGrid>
            <FormGroup><Label>Tipo de Item</Label>
              <Controller
                name={`${itemPath}.itemType`}
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    data-fieldid={`${itemPath}.itemType`}
                    hasError={!!get(errors, `${itemPath}.itemType`)}
                    onChange={e => {
                      const newType = e.target.value as 'MATERIAL' | 'PRODUCT' | 'RESOURCE' | 'SERVICE';
                      field.onChange(e);
                      handleItemTypeChange(newType);
                    }}
                  >
                    <option value="">Selecione</option>
                    <option value="MATERIAL">Materia Prima</option>
                    <option value="PRODUCT">Produto Final</option>
                    <option value="RESOURCE">Insumos</option>
                    <option value="SERVICE">Serviços</option>
                  </Select>
                )}
              />
            </FormGroup>
            <FormGroup></FormGroup>
            <FormGroup></FormGroup>
          </FormGrid>

          <Card style={{ marginTop: '16px', padding: '16px' }}>
            {renderFormByType()}
          </Card>

          {subItemFields.length > 0 && (
            <>
              {subItemFields.map((subItem, subItemIndex) => (
                <SubItem
                  key={subItem.id}
                  parentItemPath={itemPath}
                  subItemIndex={subItemIndex}
                  itemType={watch(`${itemPath}.subItems.${subItemIndex}.itemType`)}
                  onDelete={() => handleRemoveSubItem(subItemIndex)}
                  itemOptionsFromApi={itemOptionsFromApi}
                  formatOptionsList={formatOptionsList}
                  finishingOptionsList={finishingOptionsList}
                />
              ))}
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
            {itemType === 'MATERIAL' ? (
              <Button
                variant="outline"
                onClick={() => handleAddSubItem('SERVICE')}
                type="button"
              >
                <Plus size={16} style={{ marginRight: '8px' }} />
                Adicionar Serviço
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShowAddItemModal(true)}
                type="button"
              >
                <Plus size={16} style={{ marginRight: '8px' }} />
                Adicionar Produto
              </Button>
            )}
          </div>

          <PricesSection itemPath={itemPath} quantity={quantity} />

          <ActionButtonsContainer>
            <Button
              variant={isItemSaved ? "outline" : "danger-outline"}
              onClick={handleCancelClick}
              type="button"
            >
              {isItemSaved ? "Cancelar alterações" : "Cancelar"}
            </Button>
            {isItemSaved && (<Button variant="danger-outline" onClick={() => setShowDeleteConfirm(true)} type="button"><Trash2 size={16} /> Remover</Button>)}
            <Button
              variant="primary"
              onClick={handleSaveClick}
              type="button"
              style={{
                borderColor: (!isItemSaved && highlightSaveButton) ? '#F87171' : undefined,
                borderWidth: (!isItemSaved && highlightSaveButton) ? '2px' : undefined
              }}
            >
              {isItemSaved ? (
                <>
                  <Save size={18} style={{ marginRight: '8px' }} />
                  <span>Salvar alterações</span>
                </>
              ) : (
                <>
                  <Plus size={18} style={{ marginRight: '8px' }} />
                  <span>Adicionar ao pedido</span>
                </>
              )}
            </Button>
          </ActionButtonsContainer>
        </>
      )}

      <ConfirmationModal isOpen={showValidationModal} title="Por favor, corrija os seguintes campos:" message={validationErrorMessages.join('\n')} actions={[{ label: "Ok, entendi", onClick: () => setShowValidationModal(false), variant: 'danger' }]} />
      <ConfirmationModal isOpen={showDeleteConfirm} title="Tem certeza que deseja excluir este produto?" message="Essa ação não poderá ser desfeita." actions={[{ label: "Não, voltar", onClick: () => setShowDeleteConfirm(false), variant: 'outline' }, { label: "Sim, excluir", onClick: handleConfirmDelete, variant: 'danger' }]} />

      <ConfirmationModal
        isOpen={showCancelConfirm}
        title="Tem certeza que quer cancelar?"
        message={isItemSaved ? "As alterações não salvas serão descartadas e todos os dados voltará como estavam antes." : "O item será removido da lista de produtos."}
        actions={[
          { label: "Não, voltar", onClick: () => setShowCancelConfirm(false), variant: 'outline' },
          { label: isItemSaved ? "Sim, descartar alterações" : "Sim, remover", onClick: handleConfirmCancel, variant: 'danger' }
        ]}
      />

      <AddItemModal
        isOpen={showAddItemModal}
        onClose={() => setShowAddItemModal(false)}
        onSelectType={handleAddSubItem}
      />
    </>
  );
};